import * as React from "react";

import { Box, Dialog, DialogTitle, makeStyles, Theme } from "@material-ui/core";
import * as Redux from "react-redux";

import { Link, MopButton } from "client/components";
import { useMerchantModel, useTranslation } from "client/hooks";
import { errorActions } from "client/store";

import { useFormCollection } from "./FormCollectionContext";
import { FormCollection, FormCollectionList } from "./FormCollectionList";

const useStyles = makeStyles((theme: Theme) => ({
  modalRoot: {
    maxWidth: 648,
    padding: theme.spacing(6, 10),
  },
  spacer: {
    marginBottom: theme.spacing(2),
  },
}));

interface ModalManager {
  additionalLinkText: string;
  allowAdditional: boolean;
  modalBody: React.ReactNode;
  modalHeader: React.ReactNode;
}

export const FormCollectionManager: React.FC<
  Pick<FormCollection, "collection" | "collectionType" | "getLabel"> &
    ModalManager
> = ({
  additionalLinkText,
  allowAdditional,
  collection,
  collectionType,
  getLabel,
  modalBody,
  modalHeader,
}) => {
  const {
    removeFieldSetFromCollection,
    resetFieldSetToCache,
    setCachedFieldSetValues,
  } = useMerchantModel();
  const classes = useStyles();
  const dispatch = Redux.useDispatch();
  const t = useTranslation();
  const {
    currentFieldSetIndex,
    handleAddToCollection,
    modalMode,
    modalState,
    setCurrentFieldSetIndex,
    setModalMode,
    setModalState,
  } = useFormCollection();

  const handleModalClose = (
    {
      isCancelAction,
    }: {
      isCancelAction?: boolean;
    },
    reason?: string
  ) => {
    if (reason === "backdropClick") return; // disable close on backdrop click
    if (isCancelAction || reason === "escapeKeyDown") {
      if (modalMode === "add") {
        removeFieldSetFromCollection(collectionType);
        dispatch(
          errorActions.clearOwnerFieldError({
            ownerIndex: currentFieldSetIndex,
          })
        );
      } else {
        resetFieldSetToCache(collection[currentFieldSetIndex]);
      }
    }
    setCurrentFieldSetIndex(0);
    setModalState("closed");
  };

  const onEdit = (index: number) => () => {
    setModalMode("edit");
    setModalState("open");
    setCachedFieldSetValues(collection[index].toFieldValueMap());
    setCurrentFieldSetIndex(index);
  };

  const onAdd = () => {
    handleAddToCollection(collectionType);
  };

  const onDelete = (index: number) => () => {
    setModalMode("delete");
    setModalState("open");
    setCurrentFieldSetIndex(index);
  };

  return (
    <>
      <FormCollectionList
        collection={collection}
        collectionType={collectionType}
        deleteHandler={onDelete}
        editHandler={onEdit}
        getLabel={getLabel}
      />
      {allowAdditional && (
        <Box justifyContent="center" display="flex">
          <Link onClick={onAdd} linkText={additionalLinkText} />
        </Box>
      )}
      <Dialog
        aria-describedby={`manage ${collectionType}`}
        aria-labelledby={`additional-${collectionType}-modal`}
        classes={{
          paper: classes.modalRoot,
        }}
        fullWidth
        maxWidth="md"
        onClose={handleModalClose}
        open={modalState === "open"}
      >
        <Box className={classes.spacer} textAlign="center">
          <DialogTitle id={`additional-${collectionType}-modal`}>
            {modalHeader}
          </DialogTitle>
        </Box>
        <Box className={classes.spacer}>{modalBody}</Box>
        <MopButton
          mopVariant={undefined}
          onClick={() => handleModalClose({ isCancelAction: true })}
        >
          {t("cancel")}
        </MopButton>
      </Dialog>
    </>
  );
};
